

import React, { useMemo, useRef } from "react";
import JoditEditor from "jodit-react";
import {
  SCHEMA_TYPES,
  buildSchemaBlock,
  buildTemplate,
  captureContext,
  readSchemaFromElement
} from "./schemaUtils";

// tiny DOM helper
const h = (tag, style = "", props = {}, children = []) => {
  const el = document.createElement(tag);
  if (style) el.setAttribute("style", style);
  Object.assign(el, props);
  [].concat(children).forEach((c) =>
    el.appendChild(typeof c === "string" ? document.createTextNode(c) : c),
  );
  return el;
};

const btnStyle =
  "padding:6px 14px;margin-right:8px;border:1px solid #bbb;border-radius:4px;background:#fff;cursor:pointer;font-size:13px;";
const primaryStyle =
  "padding:6px 14px;margin-right:8px;border:1px solid #6b46c1;border-radius:4px;background:#6b46c1;color:#fff;cursor:pointer;font-size:13px;";

/**
 * Opens the schema dialog.
 *  - new schema:      existing = null  -> inserted below the block at the cursor
 *  - edit / delete:   existing = the card element clicked in the editor
 */
function openSchemaDialog(editor, existing, onChanged) {
  const ctx = existing ? null : captureContext(editor);
  const dlg = editor.dlg({ closeOnClickOverlay: true });
  dlg.setHeader(existing ? "Edit schema" : "Insert schema");

  const textarea = h(
    "textarea",
    "width:100%;height:260px;font:12px/1.5 monospace;padding:8px;box-sizing:border-box;",
    { spellcheck: false },
  );
  const error = h("div", "color:#c62828;font-size:12px;min-height:18px;margin:6px 0;");

  const fillTemplate = (type) => {
    textarea.value = JSON.stringify(buildTemplate(type, ctx), null, 2);
    error.textContent = "";
  };

  const wrap = h("div", "padding:12px;min-width:520px;max-width:100%;");

  if (existing) {
    textarea.value = JSON.stringify(readSchemaFromElement(existing) || {}, null, 2);
  } else {
    const select = h(
      "select",
      "width:100%;padding:6px;margin-bottom:8px;",
      {},
      SCHEMA_TYPES.map((t) => h("option", "", { value: t.value }, t.label)),
    );
    select.addEventListener("change", () => fillTemplate(select.value));
    wrap.appendChild(h("label", "font-size:12px;color:#555;", {}, "Schema type"));
    wrap.appendChild(select);
    fillTemplate(SCHEMA_TYPES[0].value);
  }

  wrap.appendChild(textarea);
  wrap.appendChild(error);

  const parse = () => {
    try {
      // accept any schema: object, array or @graph, even pasted inside <script> tags
      const raw = textarea.value.replace(/<\/?script[^>]*>/gi, "").trim();
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") {
        throw new Error("schema must be a JSON object or array");
      }
      error.textContent = "";
      return obj;
    } catch (e) {
      error.textContent = "Invalid JSON: " + e.message;
      return null;
    }
  };

  const save = () => {
    const obj = parse();
    if (!obj) return;
    const html = buildSchemaBlock(obj);
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const block = tpl.content.firstElementChild;

    if (existing) {
      existing.replaceWith(block);
    } else {
      const root = editor.editor;
      const after = ctx.anchor ? ctx.anchor.nextSibling : null;
      root.insertBefore(block, after);
      if (!block.nextElementSibling) {
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        root.appendChild(p);
      }
    }
    editor.synchronizeValues();
    onChanged(editor.value);
    dlg.close();
  };

  const remove = () => {
    existing.remove();
    editor.synchronizeValues();
    onChanged(editor.value);
    dlg.close();
  };

  const bar = h("div", "margin-top:6px;");
  bar.appendChild(h("button", primaryStyle, { type: "button", onclick: save }, existing ? "Update" : "Insert"));
  if (existing) {
    bar.appendChild(h("button", btnStyle + "color:#c62828;", { type: "button", onclick: remove }, "Delete"));
  }
  bar.appendChild(h("button", btnStyle, { type: "button", onclick: () => dlg.close() }, "Cancel"));
  wrap.appendChild(bar);

  dlg.setContent(wrap);
  dlg.open();
}

function JoditEditior({ content, setContent }) {
  // keep the latest setContent without re-creating the Jodit config on every render
  const setContentRef = useRef(setContent);
  setContentRef.current = setContent;

  const config = useMemo(
    () => ({
      minHeight: 300,

      extraButtons: [
        "|",
        {
          name: "schema",
          text: "Schema",
          tooltip: "Insert schema below the current paragraph / table",
          exec: (editor) =>
            openSchemaDialog(editor, null, (value) => setContentRef.current(value)),
        },
      ],

      events: {
        // click a schema card in the editor to edit or delete it
        afterInit: (editor) => {
          editor.editor.addEventListener("click", (e) => {
            const card = e.target.closest && e.target.closest("[data-schema-block]");
            if (card && editor.editor.contains(card)) {
              e.preventDefault();
              openSchemaDialog(editor, card, (value) => setContentRef.current(value));
            }
          });
        },
      },
    }),
    [],
  );

  return (
    <div style={{ width: "100%" }}>
      <div className="text-editor">
        <JoditEditor
          value={content}
          tabIndex={1}
          onBlur={(newContent) => {
            if (newContent !== content) setContent(newContent);
          }}
          config={config}
        />
      </div>
    </div>
  );
}

export default JoditEditior;